// src/app/modules/Integrations/pages/Integrations.jsx
// ─── Sety Integrations Module ─────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import config from '../../../../config';
import Moment from 'moment';
import {
  Link2, RefreshCw, Settings, Plus, Trash2, CheckCircle,
  AlertCircle, Clock, Users, ChevronRight,
  X, Save, ExternalLink, Activity, Layers, Shield,
  AlertTriangle, Zap, BarChart3, FileText,
  ChevronLeft, Eye, EyeOff, Copy, Check, Calendar,
  ToggleLeft, ToggleRight, PlayCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import * as actions from '../../Dashbord/_redux/dashboard/Actions';
import { useOrgConfig } from '../../../../hooks/useOrgConfig';
import { ConfirmActionModal } from '../../../components/ConfirmActionModal';
import { useFeatureGate } from '../../Dashbord/components/FeatureGate';
import { useSubscriptionStatus } from '../../../../hooks/useSubscriptionStatus';
import './integrations.scss';

// ─── Provider catalogue ───────────────────────────────────────────────────────
// logoUrl: set to your hosted asset path. Falls back to styled letter avatar.

const PROVIDERS = {
  microsoft: {
    name:      'Microsoft Azure AD',
    shortName: 'Azure AD',
    desc:      'Sync users and groups from Microsoft Entra ID via the Graph API.',
    logoUrl:   '/media/integrations/microsoft.png',
    logoBg:    '#EFF6FF',
    logoColor: '#2563EB',
    logoEmoji: 'M',
    authType:  'oauth',
    category:  'directory',
    docs:      'https://docs.microsoft.com/en-us/azure/active-directory/',
    tags:      ['SSO', 'Directory', 'OAuth2'],
  },
  google_workspace: {
    name:      'Google Workspace',
    shortName: 'Google',
    desc:      'Import employees from Google Workspace and sync group membership.',
    logoUrl:   '/media/integrations/google-logo.png',
    logoBg:    '#FEF2F2',
    logoColor: '#DC2626',
    logoEmoji: 'G',
    authType:  'oauth',
    category:  'directory',
    docs:      'https://workspace.google.com/',
    tags:      ['SSO', 'Directory', 'OAuth2'],
  },
  okta: {
    name:      'Okta',
    shortName: 'Okta',
    desc:      'Connect Okta as your IdP and provision users automatically via SCIM 2.0.',
    logoUrl:   '/media/integrations/okta.png',
    logoBg:    '#F8F8FC',
    logoColor: '#1C1C2E',
    logoEmoji: 'O',
    authType:  'scim',
    category:  'directory',
    docs:      'https://developer.okta.com/',
    tags:      ['SCIM', 'IdP', 'SSO'],
  },
  scim_generic: {
    name:      'SCIM 2.0',
    shortName: 'SCIM',
    desc:      'Connect any IdP supporting SCIM 2.0 — OneLogin, JumpCloud, Ping, and more.',
    logoUrl:   null,
    logoBg:    '#F0FDF4',
    logoColor: '#16a34a',
    logoEmoji: 'S',
    authType:  'scim',
    category:  'directory',
    docs:      null,
    tags:      ['SCIM', 'IdP'],
  },
  slack: {
    name:      'Slack',
    shortName: 'Slack',
    desc:      'Push alerts, access notifications, and incidents to Slack. Enable /sety commands and natural-language invite requests.',
    logoUrl:   '/media/integrations/slack.png',
    logoBg:    '#FFF7ED',
    logoColor: '#D97706',
    logoEmoji: 'Sl',
    authType:  'oauth',
    category:  'notifications',
    docs:      'https://api.slack.com/',
    tags:      ['Notifications', 'Bot', 'OAuth2'],
  },
  microsoft_teams: {
    name:      'Microsoft Teams',
    shortName: 'Teams',
    desc:      'Send alerts and access notifications to Teams. Use @Sety commands to manage visitors from any channel.',
    logoUrl:   '/media/integrations/teams.svg',
    logoBg:    '#EFF6FF',
    logoColor: '#4F46E5',
    logoEmoji: 'T',
    authType:  'webhook',
    category:  'notifications',
    docs:      null,
    tags:      ['Notifications', 'Bot', 'Webhook'],
  },
  hardware: {
    name:      'Hardware / Access Control',
    shortName: 'Hardware',
    desc:      'Connect Sety Frontdoor, Checkpoint, or third-party hardware via API.',
    logoUrl:   '/media/integrations/sety-black.png',
    logoBg:    '#F0EEFF',
    logoColor: '#5341C4',
    logoEmoji: 'H',
    authType:  'api_key',
    category:  'hardware',
    docs:      null,
    tags:      ['Hardware', 'Access Control'],
  },
  // PAUSED — Calendar integrations. Revisit with domain-wide delegation.
  // google_calendar: { ... },
  // microsoft_calendar: { ... },
};

// Provider keys grouped by category tab
const CATEGORY_PROVIDERS = {
  all:           Object.keys(PROVIDERS),
  directory:     ['microsoft', 'google_workspace', 'okta', 'scim_generic'],
  notifications: ['slack', 'microsoft_teams'],
  hardware:      ['hardware'],
  // calendar: ['google_calendar', 'microsoft_calendar'], // PAUSED
};

const STATUS_CFG = {
  active:         { label: 'Connected',          pill: 'active',   dot: 'active',   Icon: CheckCircle  },
  active_partial: { label: 'Synced with errors', pill: 'warning',  dot: 'warning',  Icon: AlertCircle  },
  active_failed:  { label: 'Sync failed',        pill: 'error',    dot: 'error',    Icon: AlertTriangle },
  pending_setup:  { label: 'Setup incomplete',   pill: 'pending',  dot: 'pending',  Icon: AlertCircle  },
  error:          { label: 'Disconnected',       pill: 'error',    dot: 'error',    Icon: AlertTriangle },
  inactive:       { label: 'Inactive',           pill: 'inactive', dot: 'inactive', Icon: Clock        },
  expired:        { label: 'Token Expired',      pill: 'error',    dot: 'error',    Icon: AlertCircle  },
};

// Providers that support manual sync (OAuth directory providers)
const SYNC_PROVIDERS = ['microsoft', 'google_workspace', 'okta'];

function getIntStatusKey(integration) {
  if (!integration) return 'inactive';
  const { status, last_sync_status } = integration;
  if (status === 'active') {
    if (last_sync_status === 'partial') return 'active_partial';
    if (last_sync_status === 'failed')  return 'active_failed';
    return 'active';
  }
  return status || 'inactive';
}

function isIntegrationConnected(integration) {
  if (!integration || integration.status !== 'active') return false;
  const cs = integration.credentials_summary || {};
  const { provider } = integration;
  if (provider === 'slack')                                    return !!cs.workspace_id;
  if (provider === 'microsoft_teams')                          return !!(cs.has_incoming_webhook || cs.has_webhook);
  if (provider === 'scim_generic' || provider === 'hardware')  return !!cs.has_bearer_token;
  if (provider === 'google_calendar' || provider === 'microsoft_calendar') {
    // Calendar integrations — connected if OAuth tokens exist OR watch is active.
    // Fall back to status==='active' since token fields use calendar-specific names.
    return !!(cs.has_access_token || cs.calendar_watch_active || integration.status === 'active');
  }
  return !!cs.has_access_token || !!cs.has_bearer_token;
}

const SYNC_STATUS_CFG = {
  success: { color: '#16a34a', bg: '#F0FDF4', label: 'Success' },
  partial: { color: '#D97706', bg: '#FFFBEB', label: 'Partial' },
  failed:  { color: '#DC2626', bg: '#FEF2F2', label: 'Failed'  },
  running: { color: '#5341C4', bg: '#F0EEFF', label: 'Running' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SCIM_BASE          = config.URL.replace(/\/v\d+\/?$/, '') + '/scim/v2';
const SLACK_WEBHOOK_BASE = `${config.URL}integrations/webhooks/slack`;
const TEAMS_CALLBACK_URL = `${config.URL}integrations/webhooks/teams`;

function getScimUrls(integration) {
  // Prefer URLs returned by the server; fall back to deterministic construction
  const cs = integration.credentials || {};
  const orgId = integration.organization_id || '';
  return {
    usersUrl:  cs.scim_users_url  || `${SCIM_BASE}/Users?organization_id=${orgId}`,
    groupsUrl: cs.scim_groups_url || `${SCIM_BASE}/Groups?organization_id=${orgId}`,
    baseUrl:   cs.scim_base_url   || SCIM_BASE,
  };
}

function copyToClipboard(text, cb) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => { cb(true); setTimeout(() => cb(false), 2000); }).catch(() => _fallbackCopy(text, cb));
  } else {
    _fallbackCopy(text, cb);
  }
}

function _fallbackCopy(text, cb) {
  const el = document.createElement('textarea');
  el.value = text;
  el.style.cssText = 'position:fixed;opacity:0';
  document.body.appendChild(el);
  el.select();
  try { document.execCommand('copy'); cb(true); setTimeout(() => cb(false), 2000); } catch {}
  document.body.removeChild(el);
}

// ─── Provider Logo ────────────────────────────────────────────────────────────

const ProviderLogo = ({ prov, size = 44 }) => {
  const [imgErr, setImgErr] = useState(false);
  const showImg = prov.logoUrl && !imgErr;

  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.24, flexShrink: 0,
      background: prov.logoBg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', overflow: 'hidden',
    }}>
      {showImg ? (
        <img
          src={prov.logoUrl}
          alt={prov.shortName}
          onError={() => setImgErr(true)}
          style={{ width: size * 0.6, height: size * 0.6, objectFit: 'contain' }}
        />
      ) : (
        <span style={{
          fontSize: size * 0.34, fontWeight: 800, color: prov.logoColor,
           lineHeight: 1, userSelect: 'none',
        }}>
          {prov.logoEmoji}
        </span>
      )}
    </div>
  );
};

// ─── Micro-components ─────────────────────────────────────────────────────────

const Tag = ({ label }) => (
  <span style={{
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    padding: '2px 6px', borderRadius: 20, background: '#F0EEFF', color: '#5341C4',
  }}>
    {label}
  </span>
);

const Pill = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.inactive;
  return (
    <span className={`int-pill int-pill--${cfg.pill}`}>
      <span className={`int-status-dot int-status-dot--${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const Toggle = ({ checked, onChange, disabled }) => (
  <label className="int-switch">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
    <span className="int-switch__slider" />
  </label>
);

// ─── Provider Card ────────────────────────────────────────────────────────────

const ProviderCard = ({ providerKey, prov, integration, onConnect, onSelect, onSync }) => {
  const connected = isIntegrationConnected(integration);
  const statusKey = integration ? getIntStatusKey(integration) : null;
  const hasError  = statusKey === 'error' || statusKey === 'active_failed' || statusKey === 'expired';

  return (
    <div
      className={`int-provider-card${connected ? (hasError ? ' int-provider-card--error' : ' int-provider-card--connected') : ''}`}
      onClick={() => connected ? onSelect(integration) : undefined}
      style={{ cursor: connected ? 'pointer' : 'default' }}
    >
      <div className="int-provider-card__top">
        <ProviderLogo prov={prov} size={40} />
        <div className="int-provider-card__info">
          <div className="int-provider-card__name">{prov.name}</div>
          <div className="int-provider-card__desc">{prov.desc}</div>
        </div>
        {/* {connected && (
          <div className="int-provider-card__badge">
            <Pill status={statusKey || 'inactive'} />
          </div>
        )} */}
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {prov.tags.map(t => <Tag key={t} label={t} />)}
      </div>

      {connected && integration.last_sync_at && (
        <div className="int-provider-card__meta">
          <div className="int-provider-card__meta-row">
            <RefreshCw size={10} />
            Last sync {Moment(integration.last_sync_at).fromNow()}
          </div>
          {integration.last_sync_summary?.users_found != null && (
            <div className="int-provider-card__meta-row">
              <Users size={10} />
              {integration.last_sync_summary.users_found} users
            </div>
          )}
        </div>
      )}

      <div className="int-provider-card__actions">
        {connected ? (
          <>
            <button
              className="int-btn int-btn--ghost int-btn--sm"
              style={{ flex: 1 }}
              onClick={e => { e.stopPropagation(); onSelect(integration); }}
            >
              <Settings size={11} /> Manage
            </button>
            {SYNC_PROVIDERS.includes(providerKey) && (
              <button
                className="int-btn int-btn--ghost int-btn--sm"
                title="Sync now"
                onClick={e => { e.stopPropagation(); onSync(integration); }}
              >
                <RefreshCw size={11} />
              </button>
            )}
          </>
        ) : integration?.status === 'pending_setup' ? (
          <button
            className="int-btn int-btn--ghost int-btn--sm"
            style={{ flex: 1 }}
            onClick={e => { e.stopPropagation(); onSelect(integration); }}
          >
            <AlertCircle size={11} /> Resume Setup
          </button>
        ) : (
          <button
            className="int-btn int-btn--connect"
            onClick={e => { e.stopPropagation(); onConnect(providerKey); }}
          >
            <Plus size={11} /> Connect
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Provider Grid ────────────────────────────────────────────────────────────

const ProviderGrid = ({ providerKeys, integrations, onConnect, onSelect, onSync }) => {
  const integrationByProvider = useMemo(() => {
    const map = {};
    (integrations || []).forEach(i => { map[i.provider] = i; });
    return map;
  }, [integrations]);

  return (
    <div className="int-provider-grid">
      {providerKeys.map(key => {
        const prov = PROVIDERS[key];
        if (!prov) return null;
        return (
          <ProviderCard
            key={key}
            providerKey={key}
            prov={prov}
            integration={integrationByProvider[key]}
            onConnect={onConnect}
            onSelect={onSelect}
            onSync={onSync}
          />
        );
      })}
    </div>
  );
};

// ─── Sync Logs Tab ────────────────────────────────────────────────────────────

const SyncLogsTab = ({ logs, loading, onLoadMore, hasMore }) => {
  if (loading && !logs.length) {
    return (
      <>{[1,2,3].map(i => (
        <div key={i} style={{ height: 56, background: 'linear-gradient(90deg,#F8F8FC 25%,#EDEDF3 50%,#F8F8FC 75%)', backgroundSize:'200% 100%', animation:'int-shimmer 1.3s infinite', borderBottom:'1px solid #F4F4F8' }} />
      ))}</>
    );
  }
  if (!logs.length) {
    return (
      <div style={{ padding: '36px 20px', textAlign: 'center', color: '#9CA3AF' }}>
        <Activity size={28} style={{ opacity: 0.25, marginBottom: 8 }} />
        <div style={{ fontSize: 13 }}>No sync history yet</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>Trigger a manual sync to get started</div>
      </div>
    );
  }
  return (
    <>
      {logs.map((log, i) => {
        const scfg = SYNC_STATUS_CFG[log.status] || SYNC_STATUS_CFG.failed;
        return (
          <div key={log.sync_id || i} className="int-log-row">
            <div className="int-log-row__icon" style={{ background: scfg.bg, color: scfg.color }}>
              {log.status === 'success' ? <CheckCircle size={13} />
                : log.status === 'running' ? <RefreshCw size={13} className="int-spin" />
                : log.status === 'partial' ? <AlertCircle size={13} />
                : <X size={13} />}
            </div>
            <div className="int-log-row__body">
              <div className="int-log-row__title">
                {log.triggered_by === 'manual' ? 'Manual sync' : 'Scheduled sync'}
                {log.summary?.users_found != null && (
                  <span style={{ fontWeight: 400, color: '#9CA3AF', marginLeft: 6, fontSize: 11 }}>
                    · {log.summary.users_found} users
                    {log.summary.users_created  > 0 && ` · +${log.summary.users_created} created`}
                    {log.summary.users_suspended > 0 && ` · ${log.summary.users_suspended} suspended`}
                    {log.summary.errors          > 0 && ` · ${log.summary.errors} errors`}
                  </span>
                )}
              </div>
              <div className="int-log-row__meta">
                <span style={{ display:'inline-flex',alignItems:'center',gap:3,fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:4,background:scfg.bg,color:scfg.color }}>{scfg.label}</span>
                {log.completed_at && <span>{Moment(log.completed_at).format('D MMM · h:mm A')}</span>}
              </div>
            </div>
            <div className="int-log-row__right">
              {log.duration_ms != null && <div className="int-log-row__duration">{(log.duration_ms / 1000).toFixed(1)}s</div>}
            </div>
          </div>
        );
      })}
      {hasMore && (
        <div style={{ padding: '12px 20px' }}>
          <button className="int-btn int-btn--ghost int-btn--sm" onClick={onLoadMore} style={{ width: '100%' }}>Load more</button>
        </div>
      )}
    </>
  );
};

// ─── Group Picker ─────────────────────────────────────────────────────────────
// Used inside SettingsTab and ConnectModal to select which IdP groups Sety syncs.

function GroupPicker({ integration, selectedGroups, onChange }) {
  const dispatch = useDispatch();
  const supportsGroupFetch = ['microsoft', 'google_workspace'].includes(integration?.provider);

  const [groups,       setGroups]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [scopeAll,     setScopeAll]     = useState(!selectedGroups || selectedGroups.length === 0);
  const [localSelected, setLocalSelected] = useState(new Set(selectedGroups || []));

  useEffect(() => {
    if (!supportsGroupFetch || !integration?.integration_id) return;
    setLoading(true);
    dispatch(actions.fetchAllData('integration_groups', integration.integration_id))
      .then(res => setGroups(res?.data || res || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [integration?.integration_id]);

  const toggle = (id) => {
    setLocalSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      onChange([...next]);
      return next;
    });
  };

  const handleScopeAll = (val) => {
    setScopeAll(val);
    if (val) { setLocalSelected(new Set()); onChange([]); }
  };

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {[
          { val: true,  label: 'Sync all users', desc: 'Everyone in the directory gets a Sety membership' },
          { val: false, label: 'Specific groups only', desc: 'Only members of selected groups are synced — recommended for large orgs' },
        ].map(({ val, label, desc }) => (
          <label key={String(val)} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${scopeAll === val ? '#5341C4' : '#EAEAF0'}`, background: scopeAll === val ? '#F0EEFF' : '#fff' }}>
            <input type="radio" checked={scopeAll === val} onChange={() => handleScopeAll(val)} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: scopeAll === val ? '#5341C4' : '#1C1C2E' }}>{label}</div>
              <div style={{ fontSize: 11, color: '#7E8299', marginTop: 2 }}>{desc}</div>
            </div>
          </label>
        ))}
      </div>

      {!scopeAll && (
        <div style={{ border: '1px solid #EAEAF0', borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
          {loading && (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
              <RefreshCw size={14} className="int-spin" style={{ marginRight: 6 }} />Loading groups…
            </div>
          )}
          {!loading && !supportsGroupFetch && (
            <div style={{ padding: 12 }}>
              <input
                className="int-input"
                placeholder="Paste group IDs (comma-separated)"
                value={[...localSelected].join(', ')}
                onChange={e => {
                  const ids = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  setLocalSelected(new Set(ids));
                  onChange(ids);
                }}
              />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Enter the group IDs from your IdP directory</div>
            </div>
          )}
          {!loading && supportsGroupFetch && groups.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No groups found</div>
          )}
          {!loading && groups.map(g => {
            const id    = g.id || g.group_id || g.externalId;
            const name  = g.displayName || g.name || id;
            const count = g.memberCount ?? g.members?.length;
            const selected = localSelected.has(id);
            return (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid #F0F0F5', cursor: 'pointer', background: selected ? '#F8F5FF' : 'transparent' }}>
                <input type="checkbox" checked={selected} onChange={() => toggle(id)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: selected ? 700 : 400, color: selected ? '#5341C4' : '#1C1C2E' }}>{name}</div>
                  {count != null && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{count} member{count !== 1 ? 's' : ''}</div>}
                </div>
                {selected && <CheckCircle size={13} color="#5341C4" />}
              </label>
            );
          })}
        </div>
      )}

      {!scopeAll && localSelected.size > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#5341C4', fontWeight: 600 }}>
          {localSelected.size} group{localSelected.size !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

const SettingsTab = ({ integration, spaces, onSave, saving }) => {
  const isSlack = integration.provider === 'slack';
  const isTeams = integration.provider === 'microsoft_teams';
  const isDirectory = ['microsoft', 'google_workspace', 'okta', 'scim_generic'].includes(integration.provider);

  const [settings, setSettings] = useState({
    auto_provision:               integration.settings?.auto_provision               ?? true,
    auto_deprovision:             integration.settings?.auto_deprovision             ?? false,
    delete_on_leave:              integration.settings?.delete_on_leave              ?? false,
    default_role:                 integration.settings?.default_role                 ?? 'staff',
    default_space_id:             integration.settings?.default_space_id             ?? '',
    sync_interval_minutes:        integration.settings?.sync_interval_minutes        ?? 60,
    // Slack bot channels
    access_channel:               integration.settings?.access_channel               ?? '',
    alert_channel:                integration.settings?.alert_channel                ?? '',
    notification_channel:         integration.settings?.notification_channel         ?? '',
    // Teams webhooks
    incoming_webhook_url:         integration.settings?.incoming_webhook_url         ?? '',
    alert_channel_webhook:        integration.settings?.alert_channel_webhook        ?? '',
    notification_channel_webhook: integration.settings?.notification_channel_webhook ?? '',
    outgoing_webhook_token:       '',
  });
  const [syncGroups, setSyncGroups] = useState(integration.settings?.sync_groups || []);
  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  return (
    <div>
      {!isSlack && !isTeams && (
        <>
          <div className="int-settings-section">
            <div className="int-settings-section__title">Provisioning</div>
            {[
              { key: 'auto_provision',   label: 'Auto-provision users',   desc: 'Create Sety memberships for new directory users' },
              { key: 'auto_deprovision', label: 'Auto-deprovision users', desc: 'Suspend members removed from the directory' },
              { key: 'delete_on_leave',  label: 'Delete on leave',        desc: 'Permanently delete instead of suspend' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="int-toggle-row">
                <div>
                  <div className="int-toggle-row__label">{label}</div>
                  <div className="int-toggle-row__desc">{desc}</div>
                </div>
                <Toggle
                  checked={settings[key]}
                  onChange={v => setSettings(s => ({ ...s, [key]: v }))}
                  disabled={key === 'delete_on_leave' && !settings.auto_deprovision}
                />
              </div>
            ))}
          </div>
          <div className="int-settings-section">
            <div className="int-settings-section__title">Defaults for new members</div>
            <div className="int-field">
              <label>Default Role</label>
              <select value={settings.default_role} onChange={e => setSettings(s => ({ ...s, default_role: e.target.value }))}>
                <option value="member">Member</option>
                <option value="staff">Staff</option>
                <option value="security">Security</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="int-field">
              <label>Default Space / Department</label>
              <select value={settings.default_space_id} onChange={e => setSettings(s => ({ ...s, default_space_id: e.target.value }))}>
                <option value="">None — assign manually</option>
                {(spaces || []).map(sp => <option key={sp.space_id || sp.name} value={sp.space_id || sp.name}>{sp.name}</option>)}
              </select>
            </div>
          </div>
          <div className="int-settings-section">
            <div className="int-settings-section__title">Sync Schedule</div>
            <div className="int-field">
              <label>Interval</label>
              <select value={settings.sync_interval_minutes} onChange={e => setSettings(s => ({ ...s, sync_interval_minutes: Number(e.target.value) }))}>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every hour</option>
                <option value={360}>Every 6 hours</option>
                <option value={720}>Every 12 hours</option>
                <option value={1440}>Daily</option>
              </select>
            </div>
          </div>
          {isDirectory && (
            <div className="int-settings-section">
              <div className="int-settings-section__title">Who to sync</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12, lineHeight: 1.6 }}>
                Control which users get Sety memberships. Limiting to specific groups is strongly recommended for large organisations.
              </div>
              <GroupPicker
                integration={integration}
                selectedGroups={syncGroups}
                onChange={setSyncGroups}
              />
            </div>
          )}
        </>
      )}
      {/* ── Slack bot channels ── */}
      {isSlack && (
        <>
          <div className="int-settings-section">
            <div className="int-settings-section__title">Bot Channels</div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14, lineHeight: 1.6 }}>
              Use the channel <strong>ID</strong> (starts with C), not the channel name. Find it by right-clicking a channel in Slack → <em>View channel details</em> → bottom of the About tab.
            </div>
            {[
              { key: 'access_channel',        icon: '🤖', label: 'Access commands channel',   placeholder: 'C0XXXXXX',  desc: '#sety-access — /sety commands and natural-language invite requests' },
              { key: 'alert_channel',         icon: '⚠️',  label: 'Danger alerts channel',     placeholder: 'C0YYYYYY',  desc: '#sety-alerts — automatic push alerts for security incidents' },
              { key: 'notification_channel',  icon: '👤', label: 'New hire alerts channel',    placeholder: 'C0ZZZZZZZ', desc: '#hr-team — notifications when new staff are provisioned' },
            ].map(({ key, icon, label, placeholder, desc }) => (
              <div className="int-field" key={key}>
                <label>{icon} {label}</label>
                <input type="text" placeholder={placeholder} value={settings[key] || ''}
                  onChange={e => { const v = e.target.value; set(key, v); }} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{desc}</div>
              </div>
            ))}
          </div>

          <div className="int-settings-section">
            <div className="int-settings-section__title">Slack API Dashboard Setup</div>
            <div style={{ fontSize: 12, color: '#7E8299', lineHeight: 1.7 }}>
              In your Slack app at{' '}
              <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" style={{ color: '#5341C4' }}>api.slack.com/apps</a>:
            </div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ padding: '12px 14px', background: '#F8F8FC', borderRadius: 8, fontSize: 12, color: '#3D3D5C' }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Event Subscriptions → Request URL</strong>
                <code style={{ fontSize: 11, background: '#EDEDF3', padding: '3px 8px', borderRadius: 4, display: 'block', wordBreak: 'break-all', marginBottom: 8 }}>
                  {SLACK_WEBHOOK_BASE}
                </code>
                Subscribe to:{' '}
                <code style={{ fontSize: 11, background: '#EDEDF3', padding: '2px 5px', borderRadius: 4 }}>message.channels</code>{', '}
                <code style={{ fontSize: 11, background: '#EDEDF3', padding: '2px 5px', borderRadius: 4 }}>app_mention</code>
              </div>
              <div style={{ padding: '12px 14px', background: '#F8F8FC', borderRadius: 8, fontSize: 12, color: '#3D3D5C' }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Slash Commands → Command: <code style={{ background: '#EDEDF3', padding: '2px 5px', borderRadius: 4 }}>/sety</code></strong>
                <strong>Request URL:</strong>
                <code style={{ fontSize: 11, background: '#EDEDF3', padding: '3px 8px', borderRadius: 4, display: 'block', wordBreak: 'break-all', marginTop: 4 }}>
                  {SLACK_WEBHOOK_BASE}/command
                </code>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Teams webhook settings ── */}
      {isTeams && (
        <>
          <div className="int-settings-section">
            <div className="int-settings-section__title">Webhook URLs</div>
            {[
              { key: 'incoming_webhook_url',         label: 'Incoming Webhook URL',         placeholder: 'https://xxx.webhook.office.com/...',  desc: 'Sety → Teams. Get from channel → ··· → Connectors → Incoming Webhook.' },
              { key: 'alert_channel_webhook',        label: 'Alert Channel Webhook',        placeholder: 'Optional — dedicated alerts channel',   desc: 'Separate channel for danger alerts. Uses main webhook if blank.' },
              { key: 'notification_channel_webhook', label: 'HR Channel Webhook',           placeholder: 'Optional — dedicated HR channel',       desc: 'Separate channel for new hire alerts. Uses main webhook if blank.' },
            ].map(({ key, label, placeholder, desc }) => (
              <div className="int-field" key={key}>
                <label>{label}</label>
                <input type="url" placeholder={placeholder} value={settings[key] || ''}
                  onChange={e => { const v = e.target.value; set(key, v); }} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{desc}</div>
              </div>
            ))}
          </div>
          <div className="int-settings-section">
            <div className="int-settings-section__title">Outgoing Webhook Token</div>
            <div className="int-field">
              <label>Secret Token</label>
              <input type="password" placeholder="Leave blank to keep current token"
                value={settings.outgoing_webhook_token || ''}
                onChange={e => { const v = e.target.value; set('outgoing_webhook_token', v); }} />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                Rotating the token requires updating it in Teams (Apps → Manage your apps → Outgoing Webhooks).
              </div>
            </div>
          </div>
        </>
      )}

      <div style={{ paddingTop: 16 }}>
        <button className="int-btn int-btn--primary" disabled={saving}
          onClick={() => onSave({ ...settings, sync_groups: syncGroups })}>
          {saving ? <><RefreshCw size={12} className="int-spin" /> Saving…</> : <><Save size={12} /> Save Settings</>}
        </button>
      </div>
    </div>
  );
};

// ─── Credentials Tab ──────────────────────────────────────────────────────────

const CredentialsTab = ({ integration, onGenerateToken, generatingToken, onReAuth }) => {
  const prov = PROVIDERS[integration.provider] || {};
  const [copied, setCopied] = useState({});
  const [showToken, setShowToken] = useState(false);
  // Bearer token — only present if just-generated this session
  const scimToken        = integration.credentials?.bearer_token;
  // Whether a token already exists on the server (but we can't retrieve it)
  const hasExistingToken = integration.credentials_summary?.has_bearer_token === true && !scimToken;
  const { usersUrl, groupsUrl, baseUrl } = getScimUrls(integration);
  const copy = (key, text) => copyToClipboard(text, v => setCopied(c => ({ ...c, [key]: v })));

  if (prov.authType === 'webhook') return (
    <div>
      <div className="int-alert int-alert--info">
        <Zap size={14} />
        <div>Microsoft Teams is connected via webhooks. Update URLs in the <strong>Settings</strong> tab if they change.</div>
      </div>
      <div className="int-settings-section">
        <div className="int-settings-section__title">Webhook Status</div>
        {[
          { label: 'Incoming Webhook', desc: 'Sends alerts from Sety → Teams', key: 'has_incoming_webhook' },
          { label: 'Outgoing Token',   desc: 'Receives @Sety commands from Teams',    key: 'has_outgoing_token' },
        ].map(({ label, desc, key }) => (
          <div key={key} className="int-toggle-row">
            <div>
              <div className="int-toggle-row__label">{label}</div>
              <div className="int-toggle-row__desc">{desc}</div>
            </div>
            {integration.credentials_summary?.[key]
              ? <CheckCircle size={14} color="#16a34a" />
              : <AlertCircle size={14} color="#D97706" />}
          </div>
        ))}
      </div>
    </div>
  );

  if (prov.authType === 'oauth') return (
    <div>
      <div className="int-alert int-alert--info">
        <Shield size={14} />
        <div>This integration uses OAuth 2.0. Tokens are managed automatically. Re-authorize below if the connection expires.</div>
      </div>
      {(integration.credentials_summary?.token_expires_at || integration.credentials?.token_expires_at) && (
        <div className="int-settings-section">
          <div className="int-settings-section__title">Token Status</div>
          <div className="int-toggle-row">
            <div>
              <div className="int-toggle-row__label">Expires</div>
              <div className="int-toggle-row__desc">
                {(() => {
                  const exp = integration.credentials_summary?.token_expires_at || integration.credentials?.token_expires_at;
                  return `${Moment(exp).fromNow()} (${Moment(exp).format('D MMM YYYY, h:mm A')})`;
                })()}
              </div>
            </div>
            <CheckCircle size={14} color={integration.credentials_summary?.has_access_token ? '#16a34a' : '#D97706'} />
          </div>
        </div>
      )}
      <button className="int-btn int-btn--primary" onClick={() => onReAuth?.(integration)}>
        <ExternalLink size={12} /> Re-authorize with {prov.name}
      </button>
    </div>
  );

  if (prov.authType === 'scim') return (
    <div>
      <div className="int-alert int-alert--info">
        <Link2 size={14} />
        <div>
          Paste the SCIM Base URL and Bearer token into your IdP's SCIM provisioning settings.
          The token is shown <strong>once</strong> — regenerating it will invalidate the previous one.
        </div>
      </div>

      {/* Token generation / display */}
      <div className="int-settings-section">
        <div className="int-settings-section__title">SCIM Token</div>

        {/* Token exists on server but we can't retrieve it */}
        {hasExistingToken && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0', marginBottom: 8 }}>
              <CheckCircle size={14} color="#16a34a" />
              <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>Token configured — active and working</span>
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10 }}>
              The bearer token is stored securely and cannot be retrieved. Regenerate below to get a new one — this will invalidate the existing token in your IdP.
            </div>
            <button className="int-btn int-btn--ghost int-btn--sm" onClick={() => onGenerateToken?.(integration)} disabled={generatingToken}>
              {generatingToken ? <><RefreshCw size={11} className="int-spin" /> Regenerating…</> : <><RefreshCw size={11} /> Regenerate Token</>}
            </button>
            <span style={{ fontSize: 11, color: '#D97706', marginLeft: 10 }}>Regenerating invalidates the current token immediately</span>
          </div>
        )}

        {/* No token at all */}
        {!scimToken && !hasExistingToken && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
            <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>No token generated yet</span>
            <button className="int-btn int-btn--primary int-btn--sm" onClick={() => onGenerateToken?.(integration)} disabled={generatingToken}>
              {generatingToken ? <><RefreshCw size={11} className="int-spin" /> Generating…</> : <><Zap size={11} /> Generate Token</>}
            </button>
          </div>
        )}

        {/* Token just generated this session — show it */}
        {scimToken && (
          <>
            <div className="int-alert int-alert--warning" style={{ marginBottom: 10 }}>
              <AlertTriangle size={13} />
              <div>Copy this token now — it will not be shown again after you leave this page.</div>
            </div>
            <div className="int-field">
              <label>Bearer Token</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type={showToken ? 'text' : 'password'} readOnly value={scimToken} style={{ flex: 1,  fontSize: 11 }} />
                <button className="int-icon-btn" onClick={() => setShowToken(v => !v)}>{showToken ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                <button className="int-icon-btn" onClick={() => copy('token', scimToken)}>{copied.token ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <button className="int-btn int-btn--ghost int-btn--sm" onClick={() => onGenerateToken?.(integration)} disabled={generatingToken}>
                {generatingToken ? <><RefreshCw size={11} className="int-spin" /> Regenerating…</> : <><RefreshCw size={11} /> Regenerate</>}
              </button>
              <span style={{ fontSize: 11, color: '#D97706' }}>Regenerating invalidates the current token immediately</span>
            </div>
          </>
        )}
      </div>

      {/* SCIM endpoint URLs — always show, derived from org if not in credentials */}
      <div className="int-settings-section">
        <div className="int-settings-section__title">SCIM Endpoints</div>
        {[
          { label: 'SCIM Base URL',  key: 'base',   value: baseUrl   },
          { label: 'Users URL',      key: 'users',  value: usersUrl  },
          { label: 'Groups URL',     key: 'groups', value: groupsUrl },
        ].map(({ label, key, value }) => (
          <div className="int-field" key={key}>
            <label>{label}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" readOnly value={value} style={{ flex: 1,  fontSize: 11 }} />
              <button className="int-icon-btn" onClick={() => copy(key, value)}>{copied[key] ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}</button>
            </div>
          </div>
        ))}
      </div>

      {/* Setup guide */}
      <div className="int-settings-section">
        <div className="int-settings-section__title">Setup Guide</div>
        <div style={{ fontSize: 12, color: '#7E8299', lineHeight: 1.8 }}>
          {['okta', 'scim_generic'].includes(integration.provider) && (
            <ol style={{ paddingLeft: 16, margin: 0 }}>
              <li>In your IdP, open the Sety app → <strong>Provisioning</strong> → <strong>SCIM</strong></li>
              <li>Paste the <strong>SCIM Base URL</strong> as the Tenant URL</li>
              <li>Paste the <strong>Bearer Token</strong> as the API token</li>
              <li>Set authentication method to <strong>HTTP Header</strong></li>
              <li>Enable: <em>Create Users, Update User Attributes, Deactivate Users, Push Groups</em></li>
            </ol>
          )}
          <div style={{ marginTop: 10, padding: '8px 12px', background: '#FFFBEB', borderRadius: 6, fontSize: 11, color: '#92400E', border: '1px solid #FDE68A' }}>
            <strong>Groups = Spaces:</strong> When your IdP pushes group membership, it maps to Sety Spaces. Staff added to a group are added to the corresponding Space.
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="int-settings-section">
        <div className="int-settings-section__title">Webhook URL</div>
        <div className="int-field">
          <label>Endpoint</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" readOnly value={integration.credentials?.webhook_url || `${window.location.origin}/api/v1/integrations/webhooks/${integration.provider}`} style={{ flex: 1,  fontSize: 11 }} />
            <button className="int-icon-btn" onClick={() => copy('webhook', integration.credentials?.webhook_url || '')}>{copied.webhook ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Integration Detail ───────────────────────────────────────────────────────

const IntegrationDetail = ({ integration, spaces, syncLogs, syncLogsLoading, hasMoreLogs, onBack, onSync, onDelete, onSave, onLoadMoreLogs, onGenerateToken, onReAuth, syncing, saving, generatingToken }) => {
  const [tab, setTab] = useState('overview');
  const prov    = PROVIDERS[integration.provider] || {};
  const summary = integration.last_sync_summary || {};

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="int-icon-btn" onClick={onBack}><ChevronLeft size={14} /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <ProviderLogo prov={prov} size={40} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1C1C2E' }}>{prov.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              <Pill status={getIntStatusKey(integration)} />
              {integration.last_sync_at && <span style={{ fontSize: 11, color: '#9CA3AF' }}>Last sync {Moment(integration.last_sync_at).fromNow()}</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {SYNC_PROVIDERS.includes(integration.provider) && (
            <button className="int-btn int-btn--ghost int-btn--sm" onClick={onSync} disabled={syncing}>
              <RefreshCw size={11} className={syncing ? 'int-spin' : ''} />
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
          )}
          {CALENDAR_PROVIDERS.includes(integration.provider) && (
            <button className="int-btn int-btn--ghost int-btn--sm" onClick={() => setTab('calendar_settings')}>
              <Calendar size={11} /> Configure Rules
            </button>
          )}
          <button className="int-btn int-btn--danger int-btn--sm" onClick={onDelete}><Trash2 size={11} /> Disconnect</button>
        </div>
      </div>

      {(integration.status === 'error' || integration.status === 'expired') && (
        <div className="int-alert int-alert--error">
          <AlertTriangle size={14} />
          <div><strong>Integration issue:</strong> {integration.last_error || 'An error occurred during the last sync.'}{integration.error_count > 1 && ` (${integration.error_count} consecutive failures)`}</div>
        </div>
      )}

      <div className="int-tabbar" style={{ marginBottom: 16 }}>
        {(CALENDAR_PROVIDERS.includes(integration.provider) ? [
          ['overview',          Activity,  'Overview'],
          ['calendar_settings', Calendar,  'Rules & Config'],
          ['credentials',       Shield,    'Credentials'],
        ] : [
          ['overview',     Activity,  'Overview'],
          ...(integration.provider !== 'microsoft_teams' ? [['logs', FileText, 'Sync Logs']] : []),
          ['settings',     Settings,  'Settings'],
          ['credentials',  Shield,    'Credentials'],
        ]).map(([key, Icon, label]) => (
          <button key={key} className={`int-tab ${tab === key ? 'int-tab--active' : ''}`} onClick={() => setTab(key)}>
            <Icon size={12} />{label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="int-detail">
          <div className="int-card">
            <div className="int-card__header">
              <div className="int-card__title"><BarChart3 size={13} /> Last Sync Summary</div>
              {integration.last_sync_at && <span className="int-card__meta">{Moment(integration.last_sync_at).format('D MMM · h:mm A')}</span>}
            </div>
            <div className="int-card__body">
              {integration.last_sync_at ? (
                <>
                  <div className="int-stat-row">
                    {[{label:'Found',value:summary.users_found||0,color:'#5341C4'},{label:'Created',value:summary.users_created||0,color:'#16a34a'},{label:'Updated',value:summary.users_updated||0,color:'#2563EB'}].map(s=>(
                      <div key={s.label} className="int-stat-box"><div className="int-stat-box__value" style={{'--c':s.color}}>{s.value}</div><div className="int-stat-box__label">{s.label}</div></div>
                    ))}
                  </div>
                  <div className="int-stat-row">
                    {[{label:'Suspended',value:summary.users_suspended||0,color:'#D97706'},{label:'Deleted',value:summary.users_deleted||0,color:'#DC2626'},{label:'Errors',value:summary.errors||0,color:summary.errors>0?'#DC2626':'#9CA3AF'}].map(s=>(
                      <div key={s.label} className="int-stat-box"><div className="int-stat-box__value" style={{'--c':s.color}}>{s.value}</div><div className="int-stat-box__label">{s.label}</div></div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{textAlign:'center',padding:'24px 0',color:'#9CA3AF'}}>
                  <RefreshCw size={24} style={{opacity:0.25,marginBottom:8}}/>
                  <div style={{fontSize:13}}>No sync has run yet</div>
                  <div style={{fontSize:11,marginTop:4}}>Click "Sync Now" to import your directory</div>
                </div>
              )}
            </div>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <div className="int-card">
              <div className="int-card__header"><div className="int-card__title"><Settings size={13}/> Configuration</div></div>
              <div className="int-card__body" style={{padding:'14px 16px'}}>
                {[
                  {label:'Auto-provision',   value:integration.settings?.auto_provision?'Enabled':'Disabled'},
                  {label:'Auto-deprovision', value:integration.settings?.auto_deprovision?'Enabled':'Disabled'},
                  {label:'Default role',     value:integration.settings?.default_role||'Staff'},
                  {label:'Sync interval',    value:`Every ${integration.settings?.sync_interval_minutes||60} min`},
                ].map(({label,value})=>(
                  <div key={label} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid #F4F4F8',fontSize:12}}>
                    <span style={{color:'#7E8299',fontWeight:600}}>{label}</span>
                    <span style={{color:'#1C1C2E',fontWeight:700}}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
            {prov.docs && (
              <a href={prov.docs} target="_blank" rel="noopener noreferrer" className="int-btn int-btn--ghost" style={{justifyContent:'center'}}>
                <ExternalLink size={12}/> View Docs
              </a>
            )}
          </div>
        </div>
      )}
      {tab === 'logs' && (
        <div className="int-card">
          <div className="int-card__header">
            <div className="int-card__title"><FileText size={13}/> Sync History</div>
            <button className="int-btn int-btn--ghost int-btn--sm" onClick={onSync} disabled={syncing}>
              <RefreshCw size={11} className={syncing?'int-spin':''}/> Sync Now
            </button>
          </div>
          <SyncLogsTab logs={syncLogs} loading={syncLogsLoading} hasMore={hasMoreLogs} onLoadMore={onLoadMoreLogs}/>
        </div>
      )}
      {tab === 'calendar_settings' && (
        <CalendarSettingsTab integration={integration} onSaved={() => {}} />
      )}
      {tab === 'settings' && (
        <div className="int-card">
          <div className="int-card__header"><div className="int-card__title"><Settings size={13}/> Sync Settings</div></div>
          <div className="int-card__body"><SettingsTab integration={integration} spaces={spaces} onSave={onSave} saving={saving}/></div>
        </div>
      )}
      {tab === 'credentials' && (
        <div className="int-card">
          <div className="int-card__header"><div className="int-card__title"><Shield size={13}/> Credentials &amp; Connection</div></div>
          <div className="int-card__body"><CredentialsTab integration={integration} onGenerateToken={onGenerateToken} generatingToken={generatingToken} onReAuth={onReAuth} /></div>
        </div>
      )}
    </div>
  );
};

// ─── Connect Modal ────────────────────────────────────────────────────────────

const ConnectModal = ({ show, providerKey, organizationId, onHide, onConnected }) => {
  const dispatch = useDispatch();
  const prov = PROVIDERS[providerKey] || {};
  const [loading,           setLoading]           = useState(false);
  const [step,              setStep]              = useState('intro');
  const [scimCreds,         setScimCreds]         = useState(null);
  const [createdIntegration, setCreatedIntegration] = useState(null);
  const [selectedGroups,    setSelectedGroups]    = useState([]);
  const [savingGroups,      setSavingGroups]      = useState(false);
  const [settings,          setSettings]          = useState({ auto_provision: true, auto_deprovision: false, default_role: 'staff' });
  const [copiedKey,         setCopiedKey]         = useState(null);
  const [showToken,         setShowToken]         = useState(false);
  const [teamsForm,         setTeamsForm]         = useState({ incoming_webhook_url: '', outgoing_webhook_token: '', alert_channel_webhook: '', notification_channel_webhook: '' });
  const [teamsEndpoint,     setTeamsEndpoint]     = useState('');
  const [showTeamsOptional, setShowTeamsOptional] = useState(false);

  useEffect(() => {
    if (show) {
      setStep('intro'); setScimCreds(null); setLoading(false); setShowToken(false);
      setCreatedIntegration(null); setSelectedGroups([]);
      setTeamsForm({ incoming_webhook_url: '', outgoing_webhook_token: '', alert_channel_webhook: '', notification_channel_webhook: '' });
      setTeamsEndpoint(''); setShowTeamsOptional(false);
    }
  }, [show]);

  const handleSaveGroups = async () => {
    if (!createdIntegration?.integration_id) { setStep('done'); if (onConnected) onConnected(); return; }
    setSavingGroups(true);
    try {
      await dispatch(actions.addData('update_integration',
        { settings: { ...createdIntegration.settings, sync_groups: selectedGroups } },
        createdIntegration.integration_id
      ));
    } catch (_) {}
    setSavingGroups(false);
    setStep('done');
    if (onConnected) onConnected();
  };

  const setTeams = (key, val) => setTeamsForm(f => ({ ...f, [key]: val }));

  function copyField(key, text) {
    copyToClipboard(text, v => {
      if (v) { setCopiedKey(key); setTimeout(() => setCopiedKey(null), 2000); }
    });
  }

  async function handleConnect() {
    setLoading(true);
    try {
      if (prov.authType === 'oauth') {
        const res = await dispatch(actions.addData('get_oauth_url', {
          provider: providerKey,
          organization_id: organizationId,
          redirect_uri: `${window.location.origin}/integrations/oauth/callback`,
        }));
        const url = res?.data?.url || res?.url;
        if (url) window.location.href = url;
      } else if (prov.authType === 'scim') {
        const res = await dispatch(actions.addData('scim_setup', {
          organization_id: organizationId,
          provider: providerKey,
          settings,
        }));
        const data = res?.data?.data || res?.data || res;
        setScimCreds(data);
        setCreatedIntegration({ integration_id: data?.integration_id, settings });
        setStep('credentials');
      } else if (prov.authType === 'webhook') {
        const res = await dispatch(actions.addData('teams_setup', {
          organization_id: organizationId,
          ...teamsForm,
        }));
        const data = res?.data || res;
        setTeamsEndpoint(data?.outgoing_webhook_endpoint || TEAMS_CALLBACK_URL);
        setStep('done');
        if (onConnected) onConnected();
      } else {
        await dispatch(actions.addData('create_integration', { organization_id: organizationId, provider: providerKey, settings }));
        setStep('done');
        if (onConnected) onConnected();
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  return (
    <Modal show={show} onHide={onHide} centered className="int-connect-modal">
      <div className="modal-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ProviderLogo prov={prov} size={32} />
          <h5 className="modal-title" style={{ margin: 0 }}>
            {step === 'credentials' ? `${prov.name} — Save your credentials` : `Connect ${prov.name}`}
          </h5>
        </div>
        <button type="button" className="close" onClick={onHide} disabled={loading}><span>&times;</span></button>
      </div>

      <div className="modal-body">
        {/* ── SCIM credentials step ── */}
        {step === 'credentials' && scimCreds && (
          <div>
            <div className="int-alert int-alert--warning" style={{ marginBottom: 16 }}>
              <AlertTriangle size={14} />
              <div><strong>Copy your credentials now.</strong> The Bearer Token will not be shown again after you close this window.</div>
            </div>

            {/* Bearer token */}
            <div className="int-field">
              <label>Bearer Token <span style={{ color: '#DC2626', fontSize: 10 }}>copy now</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={showToken ? 'text' : 'password'}
                  readOnly
                  value={scimCreds.bearer_token || ''}
                  style={{ flex: 1,  fontSize: 11 }}
                />
                <button className="int-icon-btn" onClick={() => setShowToken(v => !v)}>
                  {showToken ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
                <button className="int-icon-btn" onClick={() => copyField('token', scimCreds.bearer_token || '')}>
                  {copiedKey === 'token' ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                </button>
              </div>
            </div>

            {/* SCIM URLs */}
            {[
              { key: 'users_url',  label: 'SCIM Users URL',  value: scimCreds.scim_users_url  },
              { key: 'groups_url', label: 'SCIM Groups URL', value: scimCreds.scim_groups_url },
            ].filter(f => f.value).map(({ key, label, value }) => (
              <div className="int-field" key={key}>
                <label>{label}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" readOnly value={value} style={{ flex: 1,  fontSize: 11 }} />
                  <button className="int-icon-btn" onClick={() => copyField(key, value)}>
                    {copiedKey === key ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                  </button>
                </div>
              </div>
            ))}

            <div style={{ fontSize: 12, color: '#7E8299', lineHeight: 1.7, marginTop: 12, padding: '10px 12px', background: '#F8F8FC', borderRadius: 8 }}>
              <strong>Next steps:</strong>
              <ol style={{ paddingLeft: 16, margin: '6px 0 0' }}>
                <li>In your IdP, open the Sety app → <strong>Provisioning</strong> → <strong>SCIM</strong></li>
                <li>Paste the <strong>SCIM Users URL</strong> as the Tenant URL</li>
                <li>Set auth method to <strong>HTTP Header</strong>, paste the <strong>Bearer Token</strong></li>
                <li>Enable: Create Users, Update Attributes, Deactivate Users, Push Groups</li>
              </ol>
            </div>
          </div>
        )}

        {/* ── Group select step (shown after SCIM credentials are saved) ── */}
        {step === 'group_select' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1C1C2E', marginBottom: 4 }}>Who should Sety sync?</div>
              <div style={{ fontSize: 12, color: '#7E8299', lineHeight: 1.6 }}>
                Limit syncing to specific groups to avoid importing your entire directory. You can always change this later in Settings.
              </div>
            </div>
            <GroupPicker
              integration={createdIntegration}
              selectedGroups={selectedGroups}
              onChange={setSelectedGroups}
            />
          </div>
        )}

        {/* ── Done step ── */}
        {step === 'done' && (
          <div style={{ padding: '8px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: prov.authType === 'webhook' ? 20 : 0 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <CheckCircle size={26} color="#16a34a" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Teams connected!</div>
              <div style={{ fontSize: 12, color: '#7E8299', lineHeight: 1.6 }}>{prov.name} is now connected.</div>
            </div>
            {prov.authType === 'webhook' && teamsEndpoint && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', marginBottom: 8 }}>Your outgoing webhook endpoint</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" readOnly value={teamsEndpoint} style={{ flex: 1, fontSize: 11 }} />
                  <button className="int-icon-btn" onClick={() => copyField('endpoint', teamsEndpoint)}>
                    {copiedKey === 'endpoint' ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#166534', marginTop: 6, lineHeight: 1.5 }}>
                  Paste this into your Teams Outgoing Webhook callback URL if you haven't already.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Intro step ── */}
        {step === 'intro' && (
          <>
            <p style={{ fontSize: 13, color: '#7E8299', marginBottom: 16, lineHeight: 1.6 }}>{prov.desc}</p>

            {prov.authType === 'oauth' && (
              <>
                <div className="int-alert int-alert--info">
                  <ExternalLink size={14} />
                  <div>You'll be redirected to {prov.name} to authorize Sety. Make sure you're signed in as an <strong>admin</strong>. After connecting, choose which groups to sync in the integration's <strong>Settings</strong> tab — we recommend limiting to relevant groups rather than syncing your entire directory.</div>
                </div>
                <div style={{ marginTop: 18, borderTop: '1px solid #F0F0F5', paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Provisioning</div>
                  {[
                    { key: 'auto_provision',   label: 'Auto-provision new users',       desc: 'Create Sety members for users found in the directory' },
                    { key: 'auto_deprovision', label: 'Auto-deprovision removed users',  desc: 'Suspend members when they leave the directory' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="int-toggle-row">
                      <div><div className="int-toggle-row__label">{label}</div><div className="int-toggle-row__desc">{desc}</div></div>
                      <Toggle checked={settings[key]} onChange={v => setSettings(s => ({ ...s, [key]: v }))} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {prov.authType === 'scim' && (
              <div className="int-alert int-alert--info">
                <Link2 size={14} />
                <div>Sety will generate a SCIM endpoint and Bearer token. Paste them into your IdP's SCIM provisioning settings — your IdP then pushes users to Sety automatically.</div>
              </div>
            )}

            {prov.authType === 'webhook' && (
              <div>
                {/* Step 1 */}
                <div className="int-settings-section">
                  <div className="int-settings-section__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#5341C4', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</span>
                    Incoming Webhook — Sety → Teams
                  </div>
                  <p style={{ fontSize: 12, color: '#7E8299', margin: '8px 0 12px', lineHeight: 1.6 }}>
                    In Teams, open a channel → <strong>···</strong> → <strong>Connectors</strong> → <strong>Incoming Webhook</strong> → Configure → copy the URL and paste below.
                  </p>
                  <div className="int-field">
                    <label>Incoming Webhook URL <span style={{ color: '#DC2626', fontSize: 10 }}>required</span></label>
                    <input type="url" placeholder="https://xxx.webhook.office.com/..."
                      value={teamsForm.incoming_webhook_url}
                      onChange={e => setTeams('incoming_webhook_url', e.target.value)} />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="int-settings-section">
                  <div className="int-settings-section__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#5341C4', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>2</span>
                    Outgoing Webhook — Teams → Sety
                  </div>
                  <p style={{ fontSize: 12, color: '#7E8299', margin: '8px 0 10px', lineHeight: 1.6 }}>
                    In Teams: <strong>Apps → Manage your apps → Outgoing Webhooks → Create</strong>. Set the callback URL to:
                  </p>
                  <div className="int-field">
                    <label>Callback URL <span style={{ color: '#16a34a', fontSize: 10, fontWeight: 600 }}>paste this into Teams</span></label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="text" readOnly value={TEAMS_CALLBACK_URL} style={{ flex: 1, fontSize: 11 }} />
                      <button className="int-icon-btn" onClick={() => copyField('callback', TEAMS_CALLBACK_URL)}>
                        {copiedKey === 'callback' ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                      </button>
                    </div>
                  </div>
                  <div className="int-field">
                    <label>Secret Token (from Teams)</label>
                    <input type="text" placeholder="Paste the token Teams gives you"
                      value={teamsForm.outgoing_webhook_token}
                      onChange={e => setTeams('outgoing_webhook_token', e.target.value)} />
                  </div>
                </div>

                {/* Optional channels */}
                <div className="int-settings-section">
                  <button
                    type="button"
                    className="int-settings-section__title"
                    style={{ all: 'unset', cursor: 'pointer', display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', paddingBottom: 8 }}
                    onClick={() => setShowTeamsOptional(v => !v)}
                  >
                    Optional: Dedicated alert channels
                    <ChevronRight size={13} style={{ transform: showTeamsOptional ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: '#9CA3AF' }} />
                  </button>
                  {showTeamsOptional && (
                    <>
                      <div className="int-field">
                        <label>Alert Channel Webhook</label>
                        <input type="url" placeholder="Separate webhook for danger alerts"
                          value={teamsForm.alert_channel_webhook}
                          onChange={e => setTeams('alert_channel_webhook', e.target.value)} />
                      </div>
                      <div className="int-field">
                        <label>HR Channel Webhook</label>
                        <input type="url" placeholder="Separate webhook for new hire notifications"
                          value={teamsForm.notification_channel_webhook}
                          onChange={e => setTeams('notification_channel_webhook', e.target.value)} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="modal-footer">
        {step === 'credentials' ? (
          <>
            <button className="int-btn int-btn--ghost" onClick={onHide}>Close without saving</button>
            <button className="int-btn int-btn--primary" onClick={() => setStep('group_select')}>
              <Check size={12} /> I've saved my credentials
            </button>
          </>
        ) : step === 'group_select' ? (
          <>
            <button className="int-btn int-btn--ghost" onClick={() => { setStep('done'); if (onConnected) onConnected(); }}>
              Skip for now
            </button>
            <button className="int-btn int-btn--primary" onClick={handleSaveGroups} disabled={savingGroups}>
              {savingGroups ? <><RefreshCw size={12} className="int-spin" /> Saving…</> : <><Check size={12} /> Save & Finish</>}
            </button>
          </>
        ) : step === 'done' ? (
          <button className="int-btn int-btn--primary" onClick={onHide}>Done</button>
        ) : (
          <>
            <button className="int-btn int-btn--ghost" onClick={onHide} disabled={loading}>Cancel</button>
            <button className="int-btn int-btn--primary" onClick={handleConnect} disabled={loading}>
              {loading
                ? <><RefreshCw size={12} className="int-spin" /> Connecting…</>
                : prov.authType === 'oauth'
                  ? <><ExternalLink size={12} /> Authorize with {prov.shortName}</>
                  : prov.authType === 'scim'
                    ? <><Link2 size={12} /> Generate SCIM credentials</>
                    : prov.authType === 'webhook'
                      ? <><Check size={12} /> Connect Teams</>
                      : <><Plus size={12} /> Connect</>}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

// ─── Calendar Settings Tab ────────────────────────────────────────────────────

const CALENDAR_PROVIDERS = ['google_calendar', 'microsoft_calendar'];

function CalendarSettingsTab({ integration, onSaved }) {
  const dispatch = useDispatch();
  const cal = integration.settings?.calendar || {};

  const [domains,       setDomains]      = useState((cal.internal_domains  || []).join(', '));
  const [locationKws,   setLocationKws]  = useState((cal.location_keywords || []).join(', '));
  const [noticeHours,   setNoticeHours]  = useState(cal.min_notice_hours ?? 1);
  const [autoRevoke,    setAutoRevoke]   = useState(cal.auto_revoke_on_cancel !== false);
  const [passMsg,       setPassMsg]      = useState(cal.pass_message_template || '');
  const [saving,        setSaving]       = useState(false);
  const [previewing,    setPreviewing]   = useState(false);
  const [previewData,   setPreviewData]  = useState(null);
  const [settingUp,     setSettingUp]    = useState(false);

  const split = (str) => str.split(',').map(s => s.trim()).filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(actions.addData('update_calendar_settings', {
        internal_domains:       split(domains),
        location_keywords:      split(locationKws),
        min_notice_hours:       Number(noticeHours),
        auto_revoke_on_cancel:  autoRevoke,
        pass_message_template:  passMsg,
      }, integration.integration_id));
      if (onSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewData(null);
    try {
      const res = await dispatch(actions.addData('calendar_preview', { limit: 10 }, integration.integration_id));
      setPreviewData(res?.data || res || []);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSetupWatch = async () => {
    setSettingUp(true);
    try {
      await dispatch(actions.addData('calendar_setup_watch', {}, integration.integration_id));
    } finally {
      setSettingUp(false);
    }
  };

  const watchExpiry = integration.credentials_summary?.calendar_channel_expires_at
    || integration.credentials_summary?.calendar_subscription_expires_at;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Watch status */}
      <div className="int-card">
        <div className="int-card__header">
          <div className="int-card__title"><Calendar size={13} /> Watch Status</div>
          <button className="int-btn int-btn--ghost int-btn--sm" onClick={handleSetupWatch} disabled={settingUp}>
            <RefreshCw size={11} className={settingUp ? 'int-spin' : ''} />
            {settingUp ? 'Setting up…' : 'Renew Watch'}
          </button>
        </div>
        <div className="int-card__body">
          {watchExpiry ? (
            <div style={{ fontSize: 13, color: '#374151' }}>
              Watch active · expires <strong>{Moment(watchExpiry).fromNow()}</strong>
              <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8 }}>({Moment(watchExpiry).format('D MMM, HH:mm')})</span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#9CA3AF' }}>No active watch — click Renew Watch to connect.</div>
          )}
          <div style={{ marginTop: 10, fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
            Total passes auto-generated: <strong style={{ color: '#5341C4' }}>{cal.total_passes_generated || 0}</strong>
            {cal.last_event_at && <span style={{ marginLeft: 12 }}>Last event: {Moment(cal.last_event_at).fromNow()}</span>}
          </div>
        </div>
      </div>

      {/* Rules */}
      <div className="int-card">
        <div className="int-card__header">
          <div className="int-card__title"><Settings size={13} /> Pass Rules</div>
          <button className="int-btn int-btn--primary int-btn--sm" onClick={handleSave} disabled={saving}>
            <Save size={12} />{saving ? 'Saving…' : 'Save Rules'}
          </button>
        </div>
        <div className="int-card__body">

          <div className="int-settings-section">
            <div className="int-settings-section__title">Who gets a pass</div>
            <div className="int-field">
              <label>Internal email domains</label>
              <input
                type="text"
                placeholder="e.g. company.com, company.co.uk"
                value={domains}
                onChange={e => setDomains(e.target.value)}
              />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                Attendees outside these domains are treated as external guests and receive a pass. Leave empty to send passes to all attendees.
              </div>
            </div>
            <div className="int-field">
              <label>Physical location keywords</label>
              <input
                type="text"
                placeholder="e.g. Head Office, 10 Main Street, Building A"
                value={locationKws}
                onChange={e => setLocationKws(e.target.value)}
              />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                Only generate passes when the event location matches one of these. Leave empty to apply to all in-person meetings.
              </div>
            </div>
          </div>

          <div className="int-settings-section">
            <div className="int-settings-section__title">Timing & behaviour</div>
            <div className="int-toggle-row">
              <div>
                <div className="int-toggle-row__label">Auto-revoke passes on cancellation</div>
                <div className="int-toggle-row__desc">Revoke all passes for an event if it's deleted or cancelled in the calendar</div>
              </div>
              <Toggle checked={autoRevoke} onChange={setAutoRevoke} />
            </div>
            <div className="int-field" style={{ marginTop: 12 }}>
              <label>Minimum notice (hours)</label>
              <input
                type="number"
                min={0}
                max={48}
                value={noticeHours}
                onChange={e => setNoticeHours(e.target.value)}
                style={{ maxWidth: 120 }}
              />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                Don't generate a pass if the meeting starts in less than this many hours.
              </div>
            </div>
          </div>

          <div className="int-settings-section" style={{ borderBottom: 0, paddingBottom: 0 }}>
            <div className="int-settings-section__title">Pass message</div>
            <div className="int-field">
              <label>Text added to calendar invite after pass links</label>
              <input
                type="text"
                placeholder="Your Sety visitor pass is ready. Show this at reception."
                value={passMsg}
                onChange={e => setPassMsg(e.target.value)}
              />
            </div>
          </div>

        </div>
      </div>

      {/* Preview / test mode */}
      <div className="int-card">
        <div className="int-card__header">
          <div className="int-card__title"><PlayCircle size={13} /> Test Mode — Preview Matching Events</div>
          <button className="int-btn int-btn--ghost int-btn--sm" onClick={handlePreview} disabled={previewing}>
            <RefreshCw size={11} className={previewing ? 'int-spin' : ''} />
            {previewing ? 'Loading…' : 'Run Preview'}
          </button>
        </div>
        {previewData && (
          <div className="int-card__body" style={{ padding: 0 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8F8FC' }}>
                  {['Event', 'Start', 'External guests', 'Pass?'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '20px 14px', color: '#9CA3AF', textAlign: 'center' }}>No upcoming events found</td></tr>
                )}
                {previewData.map((ev, i) => (
                  <tr key={ev.event_id || i} style={{ borderTop: '1px solid #EAEAF0' }}>
                    <td style={{ padding: '9px 14px', fontWeight: 600, color: '#1C1C2E', maxWidth: 200 }}>{ev.title || '(no title)'}</td>
                    <td style={{ padding: '9px 14px', color: '#6B7280' }}>{ev.start ? Moment(ev.start).format('D MMM · HH:mm') : '—'}</td>
                    <td style={{ padding: '9px 14px', color: '#374151' }}>{ev.external_count}</td>
                    <td style={{ padding: '9px 14px' }}>
                      {ev.would_generate
                        ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 11 }}>✓ Yes</span>
                        : <span style={{ color: '#DC2626', fontSize: 11 }}>{ev.skip_reason?.replace(/_/g, ' ')}</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export function Integrations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { org } = useOrgConfig();
  const { guardAction } = useFeatureGate();
  useSubscriptionStatus(); // ensures subscription is fetched on any entry point, not just SecurityDashboard

  const { integrations, spaces } = useSelector(s => ({
    integrations: s.dashboard.integrations || [],
    spaces:       s.dashboard.space        || [],
  }), shallowEqual);

  const [selected,        setSelected]        = useState(null);
  const [connectProvider, setConnectProvider] = useState(null);
  const [syncLogs,        setSyncLogs]        = useState([]);
  const [syncLogsLoading, setSyncLogsLoading] = useState(false);
  const [hasMoreLogs,     setHasMoreLogs]     = useState(false);
  const [logsPage,        setLogsPage]        = useState(1);
  const [syncing,         setSyncing]         = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [deleteLoading,   setDeleteLoading]   = useState(false);
  const [tab,             setTab]             = useState('all');
  const [pageLoading,     setPageLoading]     = useState(false);

  // ── Handle OAuth redirect-back with ?connected=true ───────────────────────
  useEffect(() => {
    const connected = searchParams.get('connected');
    const provider  = searchParams.get('provider');
    if (connected === 'true') {
      const name = provider ? (PROVIDERS[provider]?.name || provider) : 'Integration';
      toast.success(`${name} connected successfully`);
      navigate('/integrations', { replace: true });
    }
  }, []);

  // ── Mount guard — prevents setState on unmounted component ────────────────
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => { if (org?.organization_id) load(); }, [org?.organization_id]);

  function load() {
    if (!isMountedRef.current) return;
    setPageLoading(true);
    Promise.all([
      dispatch(actions.fetchAllData('integrations', org.organization_id)),
      dispatch(actions.fetchAllData('spaces', org.organization_id)),
    ]).finally(() => {
      if (isMountedRef.current) setPageLoading(false);
    });
  }

  useEffect(() => {
    if (selected?.integration_id) loadSyncLogs(selected.integration_id, 1, true);
  }, [selected?.integration_id]);

  async function loadSyncLogs(integrationId, page = 1, reset = false) {
    if (!isMountedRef.current) return;
    setSyncLogsLoading(true);
    try {
      const res = await dispatch(
        actions.fetchAllData('integration_sync_logs', integrationId, { page, limit: 20 })
      );
      if (!isMountedRef.current) return;
      if (res?.logs) {
        setSyncLogs(prev => reset ? res.logs : [...prev, ...res.logs]);
        setHasMoreLogs(res.logs.length === 20);
        setLogsPage(page);
      }
    } finally {
      if (isMountedRef.current) setSyncLogsLoading(false);
    }
  }

  async function handleSync(integration) {
    if (!isMountedRef.current) return;
    setSyncing(true);
    let syncTimer;
    try {
      await dispatch(
        actions.addData(
          'trigger_sync',
          { organization_id: org.organization_id },
          integration.integration_id
        )
      );
      // Sync is async on the backend — poll after a short delay
      syncTimer = setTimeout(() => {
        if (!isMountedRef.current) return;
        load();
        if (selected?.integration_id === integration.integration_id) {
          loadSyncLogs(integration.integration_id, 1, true);
        }
      }, 2000);
    } finally {
      if (isMountedRef.current) setSyncing(false);
    }
    // Return cleanup so callers can cancel the timer if needed
    return () => clearTimeout(syncTimer);
  }

  async function handleSaveSettings(updatedSettings) {
    if (!selected || !isMountedRef.current) return;
    setSaving(true);
    try {
      await dispatch(
        actions.addData(
          'update_integration',
          { settings: updatedSettings },
          selected.integration_id
        )
      );
      if (isMountedRef.current) load();
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }

  async function handleGenerateScimToken(integration) {
    if (!isMountedRef.current) return;
    setGeneratingToken(true);
    try {
      const res = await dispatch(actions.addData('generate_scim_token', {}, integration.integration_id));
      if (res && isMountedRef.current) {
        // Spec returns { bearer_token, scim_base_url, scim_users_url, scim_groups_url }
        const tokenData = res?.data || res;
        setSelected(prev => prev ? {
          ...prev,
          credentials: { ...prev.credentials, ...tokenData },
        } : prev);
      }
    } finally {
      if (isMountedRef.current) setGeneratingToken(false);
    }
  }

  async function handleReAuth(integration) {
    if (!isMountedRef.current) return;
    try {
      const res = await dispatch(actions.addData('get_oauth_url', {
        provider:        integration.provider,
        organization_id: org.organization_id,
        redirect_uri:    `${window.location.origin}/integrations/oauth/callback`,
      }));
      const url = res?.data?.url || res?.url;
      if (url) window.location.href = url;
    } catch (e) { console.error(e); }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget || !isMountedRef.current) return;
    setDeleteLoading(true);
    try {
      await dispatch(
        actions.addData(
          'delete_integration',
          { organization_id: org.organization_id },
          deleteTarget.integration_id
        )
      );
      if (!isMountedRef.current) return;
      setDeleteTarget(null);
      setSelected(null);
      load();
    } finally {
      if (isMountedRef.current) setDeleteLoading(false);
    }
  }

  // ── Tab filtering ─────────────────────────────────────────────────────────
  const visibleProviderKeys = CATEGORY_PROVIDERS[tab] || CATEGORY_PROVIDERS.all;

  const connectedInTab = useMemo(() =>
    (integrations || []).filter(i => visibleProviderKeys.includes(i.provider) && isIntegrationConnected(i)),
    [integrations, visibleProviderKeys]
  );

  const connectedCount = (integrations || []).filter(isIntegrationConnected).length;

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className="int-page">
        <IntegrationDetail
          integration={selected} spaces={spaces} syncLogs={syncLogs}
          syncLogsLoading={syncLogsLoading} hasMoreLogs={hasMoreLogs}
          onBack={() => setSelected(null)} onSync={() => handleSync(selected)}
          onDelete={() => setDeleteTarget(selected)} onSave={handleSaveSettings}
          onLoadMoreLogs={() => loadSyncLogs(selected.integration_id, logsPage + 1)}
          onGenerateToken={handleGenerateScimToken}
          onReAuth={handleReAuth}
          syncing={syncing} saving={saving} generatingToken={generatingToken}
        />
        <ConfirmActionModal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} onConfirm={handleDeleteConfirm}
          title="Disconnect Integration"
          message={<div><p>Disconnect <strong>{PROVIDERS[deleteTarget?.provider]?.name}</strong>?</p><p className="mt-2 text-muted" style={{fontSize:12}}>Syncing will stop. Existing memberships are preserved.</p></div>}
          confirmText="Disconnect" type="danger" loading={deleteLoading}
        />
      </div>
    );
  }

  return (
    <div className="int-page">

      {/* Header */}
      <div className="int-header">
        <div className="int-header__left">
          <h1>Integrations</h1>
          <p>{org?.name} · {connectedCount} connected integration{connectedCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="int-header__actions">
          <button className="int-btn int-btn--ghost" onClick={load} disabled={pageLoading}>
            <RefreshCw size={13} className={pageLoading ? 'int-spin' : ''} />
            {pageLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Connected', value: connectedCount,                                                                                                                   bg: '#F0EEFF', color: '#5341C4', Icon: Link2         },
          { label: 'Active',    value: (integrations||[]).filter(i=>i.status==='active' && i.last_sync_status !== 'failed').length,                                     bg: '#F0FDF4', color: '#16a34a', Icon: CheckCircle   },
          { label: 'Errors',    value: (integrations||[]).filter(i=>['error','expired'].includes(i.status) || (i.status==='active' && i.last_sync_status==='failed')).length, bg: '#FEF2F2', color: '#DC2626', Icon: AlertTriangle },
          { label: 'Available', value: Object.keys(PROVIDERS).length,                                                   bg: '#F8F8FC', color: '#7E8299', Icon: Zap           },
        ].map(({ label, value, bg, color, Icon }) => (
          <div key={label} className="int-card" style={{ padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={15} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1, letterSpacing: -0.5, }}>{value}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 3 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Category tabs */}
      <div className="int-tabbar">
        {[
          ['all',           Layers,    'All',              CATEGORY_PROVIDERS.all.length],
          ['directory',     Users,     'Directory Sync',   CATEGORY_PROVIDERS.directory.length],
          ['notifications', Activity,  'Notifications',    CATEGORY_PROVIDERS.notifications.length],
          // ['calendar', Calendar, 'Calendar', 0], // PAUSED
          ['hardware',      Shield,    'Hardware',         CATEGORY_PROVIDERS.hardware.length],
        ].map(([key, Icon, label, count]) => (
          <button key={key} className={`int-tab ${tab === key ? 'int-tab--active' : ''}`} onClick={() => setTab(key)}>
            <Icon size={12} /> {label}
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, marginLeft: 2,
              background: tab === key ? 'rgba(83,65,196,0.12)' : '#F0F0F5',
              color:      tab === key ? '#5341C4' : '#9CA3AF',
            }}>{count}</span>
          </button>
        ))}
      </div>

      {/* Connected strip — scoped to current tab */}
      {/* {connectedInTab.length > 0 && (
        <div className="int-card">
          <div className="int-card__header">
            <div className="int-card__title"><CheckCircle size={13}/> Connected</div>
            <span className="int-card__meta">{connectedInTab.length} integration{connectedInTab.length !== 1 ? 's' : ''}</span>
          </div>
          {connectedInTab.map((integration, i) => {
            const prov   = PROVIDERS[integration.provider] || {};
            const isLast = i === connectedInTab.length - 1;
            return (
              <div key={integration.integration_id}
                style={{ display:'flex', alignItems:'center', gap:14, padding:'13px 20px', borderBottom: isLast ? 'none' : '1px solid #F4F4F8', cursor:'pointer', transition:'background 0.1s' }}
                onClick={() => setSelected(integration)}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <ProviderLogo prov={prov} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1C2E', marginBottom: 3 }}>{prov.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#9CA3AF', flexWrap: 'wrap' }}>
                    <Pill status={getIntStatusKey(integration)} />
                    {integration.last_sync_at && <span>Last sync {Moment(integration.last_sync_at).fromNow()}</span>}
                    {integration.last_sync_summary?.users_found != null && <span>{integration.last_sync_summary.users_found} users</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  {SYNC_PROVIDERS.includes(integration.provider) && (
                    <button className="int-btn int-btn--ghost int-btn--sm" onClick={e => { e.stopPropagation(); handleSync(integration); }} disabled={syncing}>
                      <RefreshCw size={10} className={syncing?'int-spin':''}/>
                    </button>
                  )}
                  <ChevronRight size={13} color="#DDDDE8"/>
                </div>
              </div>
            );
          })}
        </div>
      )} */}

      {/* Provider grid — filtered by active tab */}
      <div className="int-card">
        <div className="int-card__header">
          <div className="int-card__title"><Zap size={13}/> Available Integrations</div>
          <span className="int-card__meta">{visibleProviderKeys.length} provider{visibleProviderKeys.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="int-card__body">
          <ProviderGrid
            providerKeys={visibleProviderKeys}
            integrations={integrations}
            onConnect={(key) => guardAction(() => setConnectProvider(key), 'has_integrations')}
            // onConnect={(key) =>  setConnectProvider(key)}
            onSelect={setSelected}
            onSync={handleSync}
          />
        </div>
      </div>

      <ConnectModal
        show={!!connectProvider} providerKey={connectProvider}
        organizationId={org?.organization_id}
        onHide={() => setConnectProvider(null)}
        onConnected={() => { setConnectProvider(null); load(); }}
      />

      <ConfirmActionModal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} onConfirm={handleDeleteConfirm}
        title="Disconnect Integration"
        message={<div><p>Disconnect <strong>{PROVIDERS[deleteTarget?.provider]?.name}</strong>?</p><p className="mt-2 text-muted" style={{fontSize:12}}>Syncing will stop. Existing memberships are preserved.</p></div>}
        confirmText="Disconnect" type="danger" loading={deleteLoading}
      />
    </div>
  );
}